// panelPositionsSlice.js - FIXED VERSION (no unnecessary object creation)
import { createSlice } from '@reduxjs/toolkit';
import { getDefaultLayout } from '../components/dashboard/dashboard';

const initialState = {
  panelLayouts: {}, // { [userId]: { [panelId]: { x, y, width, height, activeTab } } }
  tableStates: {},   // { [userId]: { [tableId]: { columnWidths, sortBy, searchColumn, searchValue } } }
};

const panelPositionsSlice = createSlice({
  name: 'panelPositions',
  initialState,
  reducers: {
    // ========== EXISTING PANEL POSITION ACTIONS ==========
    updatePanelPosition: (state, action) => {
      const { panelId, position, size, userId } = action.payload;
      if (!userId || !panelId) return;

      if (!state.panelLayouts[userId]) state.panelLayouts[userId] = {};

      state.panelLayouts[userId][panelId] = {
        ...(state.panelLayouts[userId][panelId] || {}),
        ...(position && { x: position.x, y: position.y }),
        ...(size && { width: size.width, height: size.height }),
      };

      try {
        localStorage.setItem(`panelPositions_${userId}`, JSON.stringify(state.panelLayouts[userId]));
      } catch (err) {
        console.warn(`Failed to save panel positions for ${userId}:`, err);
      }
    },

    setAllPanelPositions: (state, action) => {
      const { userId, layout } = action.payload || {};
      if (!userId) return;
            // state.panelLayouts[userId] = layout || {};
            state.panelLayouts[userId] = {
        ...state.panelLayouts[userId],
        ...layout
      };
    },

    resetToDefaultLayout: (state, action) => {
      const userId = action.payload;
      if (!userId) return;

      try {
        localStorage.setItem(`panelPositions_${userId}`, JSON.stringify(getDefaultLayout()));
      } catch (err) {
        console.warn(`Failed to save default layout for ${userId}:`, err);
      }

      state.panelLayouts[userId] = { ...getDefaultLayout() };
    },

    resetPanelPositions: (state, action) => {
      const userId = action?.payload;
      if (userId) {
        delete state.panelLayouts[userId];
        localStorage.removeItem(`panelPositions_${userId}`);
      } else {
        Object.keys(state.panelLayouts).forEach(uid => {
          localStorage.removeItem(`panelPositions_${uid}`);
        });
        state.panelLayouts = {};
      }
    },

    setActiveTab: (state, action) => {
      const { userId, panelId, tabId } = action.payload || {};

      if (!userId || !panelId) return;
      if (!state.panelLayouts[userId]) state.panelLayouts[userId] = {};
      state.panelLayouts[userId][panelId] = {
        ...(state.panelLayouts[userId][panelId] || {}),
        activeTab: tabId,
      };
      try {
        localStorage.setItem(`panelPositions_${userId}`, JSON.stringify(state.panelLayouts[userId]));
      } catch (err) {
        console.warn(`Failed to save activeTab for ${userId}:`, err);
      }
    },

    // start of changes
  updateTableColumnWidths: (state, action) => {
    const { userId, tableId, columnWidths } = action.payload;
  
    if (!userId || !tableId) return;
    if (!state.tableStates[userId]) state.tableStates[userId] = {};
    
    // ✅ FIX: Merge with existing properties
    state.tableStates[userId][tableId] = {
      ...state.tableStates[userId][tableId], // Keep existing sortBy, search
      columnWidths
    };

    try {
      localStorage.setItem(`tableStates_${userId}`, JSON.stringify(state.tableStates[userId]));
    } catch (err) {
      console.warn(`Failed to save table column widths for ${userId}:`, err);
    }
  },

  updateTableSorting: (state, action) => {
    const { userId, tableId, sortBy } = action.payload;
    if (!userId || !tableId) return;

    if (!state.tableStates[userId]) state.tableStates[userId] = {};
    
    // ✅ FIX: Merge with existing properties
    state.tableStates[userId][tableId] = {
      ...state.tableStates[userId][tableId], // Keep existing columnWidths, search
      sortBy
    };

    try {
      localStorage.setItem(`tableStates_${userId}`, JSON.stringify(state.tableStates[userId]));
    } catch (err) {
      console.warn(`Failed to save table sorting for ${userId}:`, err);
    }
  },

  updateTableSearch: (state, action) => {
    const { userId, tableId, searchColumn, searchValue } = action.payload;
    if (!userId || !tableId) return;

    if (!state.tableStates[userId]) state.tableStates[userId] = {};
    
    // ✅ FIX: Merge with existing properties
    state.tableStates[userId][tableId] = {
      ...state.tableStates[userId][tableId], // Keep existing columnWidths, sortBy
      searchColumn,
      searchValue
    };

    try {
      localStorage.setItem(`tableStates_${userId}`, JSON.stringify(state.tableStates[userId]));
    } catch (err) {
      console.warn(`Failed to save table search for ${userId}:`, err);
    }
  },

    // Reset all table states for a user
    resetTableStates: (state, action) => {
      const userId = action.payload;
      if (userId) {
        delete state.tableStates[userId];
        localStorage.removeItem(`tableStates_${userId}`);
      } else {
        Object.keys(state.tableStates).forEach(uid => {
          localStorage.removeItem(`tableStates_${uid}`);
        });
        state.tableStates = {};
      }
    },

    // Load all table states for a user
    setAllTableStates: (state, action) => {
      const { userId, tableStates } = action.payload || {};
      if (!userId) return;
      state.tableStates[userId] = tableStates || {};
    },
  },
});

export const {
  updatePanelPosition,
  setAllPanelPositions,
  resetToDefaultLayout,
  resetPanelPositions,
  setActiveTab,
  updateTableColumnWidths,
  updateTableSorting,
  updateTableSearch,
  resetTableStates,
  setAllTableStates,
} = panelPositionsSlice.actions;

export default panelPositionsSlice.reducer;

// ========== THUNKS ==========

// Load panel positions (existing)
export const loadPanelPositionsForUser = (userId, defaultLayout = {}) => (dispatch) => {
  const key = `panelPositions_${userId}`;
  try {
    const stored = localStorage.getItem(key);
    let layout = stored ? JSON.parse(stored) : {};

    for (const panelId in defaultLayout) {
      if (!layout[panelId]) layout[panelId] = defaultLayout[panelId];
    }

    dispatch(setAllPanelPositions({ userId, layout }));
  } catch (error) {
    console.warn(`Error loading panel positions for ${userId}:`, error);
    dispatch(setAllPanelPositions({ userId, layout: { ...defaultLayout } }));
  }
};

// Load table states for a user
// start of change
// In panelPositionsSlice.js, in loadTableStatesForUser:
export const loadTableStatesForUser = (userId) => (dispatch) => {
  const key = `tableStates_${userId}`;
  // console.log(`[LOAD] Loading table states for ${userId}, key: ${key}`);
  
  try {
    const stored = localStorage.getItem(key);
    // console.log(`[LOAD] Raw stored data:`, stored);
    
    const tableStates = stored ? JSON.parse(stored) : {};
    // console.log(`[LOAD] Parsed table states:`, tableStates);
    
    dispatch(setAllTableStates({ userId, tableStates }));
  } catch (error) {
    console.warn(`Error loading table states for ${userId}:`, error);
    dispatch(setAllTableStates({ userId, tableStates: {} }));
  }
};

// Save all panel positions (existing)
export const saveAllPanelPositionsForUser = (userId) => (dispatch, getState) => {
  const layout = getState().panelPositions.panelLayouts[userId] || {};
  try {
    localStorage.setItem(`panelPositions_${userId}`, JSON.stringify(layout));
  } catch (error) {
    console.warn(`Failed to save full layout for ${userId}:`, error);
  }
};

// Save all table states
// In panelPositionsSlice.js, in saveAllTableStatesForUser:
export const saveAllTableStatesForUser = (userId) => (dispatch, getState) => {
  const tableStates = getState().panelPositions.tableStates[userId] || {};
  // console.log(`[SAVE ALL] Saving table states for ${userId}:`, tableStates);
  
  try {
    localStorage.setItem(`tableStates_${userId}`, JSON.stringify(tableStates));
    // console.log(`[SAVE ALL] Saved to localStorage key tableStates_${userId}`);
  } catch (error) {
    console.warn(`Failed to save table states for ${userId}:`, error);
  }
};